// AdminMessage.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Trash2, Edit, Plus, Check, X, Search } from "lucide-react";

const AdminMessage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form states for create/edit
  const [form, setForm] = useState({
    user_id: "",
    order_id: "",
    title: "",
    message: "",
  });
  const [editingId, setEditingId] = useState(null);

  // Search & filter
  const [searchTerm, setSearchTerm] = useState("");

  // Check admin access
  const userStr = localStorage.getItem("erpUser");
  const user = userStr ? JSON.parse(userStr) : null;
  const isAdmin = user?.level === "admin";

  // Fetch all messages
  const fetchMessages = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log("Fetching all messages as admin...");
      const res = await axios.get("https://api.kamulyaan.id/msg?limit=100");

      console.log("Raw API response:", res.data); // ← debug – remove after test

      // Robust parsing – try every possible shape
      let list = res.data?.data ?? res.data?.messages ?? res.data ?? [];

      // If single object → wrap in array
      if (list && !Array.isArray(list) && typeof list === "object") {
        list = [list];
      }

      // Final safety: must be array
      if (!Array.isArray(list)) {
        list = [];
      }

      setMessages(list);
    } catch (err) {
      console.error("Fetch all messages failed:", err);
      const errorMsg = err.response
        ? `Server error ${err.response.status}: ${err.response.data?.message || "Unknown"}`
        : "Tidak bisa terhubung ke server. Cek koneksi atau API status.";
      setError(errorMsg);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchMessages();
    }
  }, [isAdmin]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.message) {
      alert("User ID dan pesan wajib diisi");
      return;
    }

    try {
      if (editingId) {
        // Simulate update (delete + create) – add real PUT later
        await axios.delete(`https://api.kamulyaan.id/msg/${editingId}`);
        await axios.post("https://api.kamulyaan.id/msg", form);
        alert("Pesan berhasil diperbarui");
      } else {
        await axios.post("https://api.kamulyaan.id/msg", form);
        alert("Pesan berhasil dikirim");
      }

      setForm({ user_id: "", order_id: "", title: "", message: "" });
      setEditingId(null);
      fetchMessages();
    } catch (err) {
      alert("Gagal menyimpan pesan: " + (err.response?.data?.message || err.message));
    }
  };

  const handleEdit = (msg) => {
    setForm({
      user_id: msg.user_id,
      order_id: msg.order_id || "",
      title: msg.title || "",
      message: msg.message,
    });
    setEditingId(msg.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Yakin hapus pesan ini?")) return;

    try {
      await axios.delete(`https://api.kamulyaan.id/msg/${id}`);
      alert("Pesan dihapus");
      fetchMessages();
    } catch (err) {
      alert("Gagal hapus pesan");
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.post("https://api.kamulyaan.id/msg/mark-read", {
        message_id: id,
      });
      fetchMessages();
    } catch (err) {
      alert("Gagal ubah status baca");
    }
  };

  const filteredMessages = messages.filter((m) =>
    [m.user_id, m.title, m.message, m.order_id?.toString()].some((v) =>
      v?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md">
          <X size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Akses Ditolak</h2>
          <p className="text-gray-600">Halaman ini hanya untuk admin.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-8">Admin - Kelola Pesan</h1>

      {/* Form */}
      <div className="bg-white p-6 rounded-xl shadow-md mb-10">
        <h2 className="text-xl font-semibold mb-4">
          {editingId ? "Edit Pesan" : "Kirim Pesan Baru"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">User ID</label>
            <input
              type="text"
              name="user_id"
              value={form.user_id}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md p-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Order ID (opsional)</label>
            <input
              type="text"
              name="order_id"
              value={form.order_id}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Judul</label>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Pesan</label>
            <textarea
              name="message"
              value={form.message}
              onChange={handleInputChange}
              rows="4"
              className="mt-1 block w-full border border-gray-300 rounded-md p-2"
              required
            />
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
            >
              {editingId ? "Update Pesan" : "Kirim Pesan"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setForm({ user_id: "", order_id: "", title: "", message: "" });
                  setEditingId(null);
                }}
                className="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600"
              >
                Batal Edit
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Messages List */}
      <div className="bg-white p-6 rounded-xl shadow-md">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Daftar Semua Pesan</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Cari user ID, judul, pesan..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-64"
            />
          </div>
        </div>

        {loading ? (
          <p className="text-center text-gray-600">Memuat pesan...</p>
        ) : error ? (
          <p className="text-center text-red-600 bg-red-50 p-4 rounded-lg">{error}</p>
        ) : filteredMessages.length === 0 ? (
          <p className="text-center text-gray-500">Tidak ada pesan ditemukan</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-3 text-left">ID</th>
                  <th className="p-3 text-left">User ID</th>
                  <th className="p-3 text-left">Judul</th>
                  <th className="p-3 text-left">Pesan</th>
                  <th className="p-3 text-left">Order ID</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Tanggal</th>
                  <th className="p-3 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredMessages.map((msg) => (
                  <tr key={msg.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">{msg.id}</td>
                    <td className="p-3">{msg.user_id}</td>
                    <td className="p-3">{msg.title || "-"}</td>
                    <td className="p-3 max-w-xs truncate">{msg.message}</td>
                    <td className="p-3">{msg.order_id || "-"}</td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          msg.is_read ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {msg.is_read ? "Dibaca" : "Belum Dibaca"}
                      </span>
                    </td>
                    <td className="p-3">
                      {new Date(msg.created_at).toLocaleString("id-ID")}
                    </td>
                    <td className="p-3 text-center whitespace-nowrap">
                      <button
                        onClick={() => handleEdit(msg)}
                        className="text-blue-600 hover:text-blue-800 mr-3"
                        title="Edit"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(msg.id)}
                        className="text-red-600 hover:text-red-800 mr-3"
                        title="Hapus"
                      >
                        <Trash2 size={18} />
                      </button>
                      {!msg.is_read && (
                        <button
                          onClick={() => markAsRead(msg.id)}
                          className="text-green-600 hover:text-green-800"
                          title="Tandai sudah dibaca"
                        >
                          <Check size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMessage;